import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, MessageCircle, ShieldCheck } from "lucide-react";
import axios from "axios";
import { API, errText } from "@/lib/api";
import { BRAND, BUDGETS, CORRIDORS, TIMELINES, waLink } from "@/config/brand";
import useAttribution from "@/hooks/useAttribution";

const EMPTY = { name: "", phone: "", corridor: "wave_city", budget: BUDGETS[1], timeline: TIMELINES[0] };

const fieldCls =
  "focus-brand w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink placeholder:text-muted2 outline-none transition-colors";
const labelCls = "mb-1.5 block text-xs font-semibold text-body";
const labelHideMobile = "mb-1.5 hidden text-xs font-semibold text-body sm:block";

export const LeadForm = ({ compact = false }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const attribution = useAttribution();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[+0-9\s-]{10,15}$/.test(form.phone.trim())) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/leads`, { ...form, attribution });
      setDone(true);
      toast.success(data.message);
      if (window.gtag && BRAND.ads.conversionId) {
        window.gtag("event", "conversion", {
          send_to: `${BRAND.ads.conversionId}/${BRAND.ads.conversionLabel}`,
        });
      }
      setForm(EMPTY);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        data-testid="lead-form-success"
        className="card-soft p-8 text-center shadow-xl"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-mint">
          <Check className="h-6 w-6 text-brandDark" />
        </div>
        <h3 className="mt-5 font-display text-2xl font-bold text-ink">Request received</h3>
        <p className="mt-2.5 text-sm leading-relaxed text-body">
          An advisor will call you from {BRAND.phoneDisplay} within 30 minutes during business hours.
        </p>
        <a
          href={waLink()}
          target="_blank"
          rel="noreferrer"
          data-testid="success-whatsapp-btn"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-brandDark"
        >
          <MessageCircle className="h-4 w-4" /> Chat on WhatsApp now
        </a>
        <button
          type="button"
          data-testid="lead-form-reset"
          onClick={() => setDone(false)}
          className="mt-4 block w-full text-xs font-medium text-body underline-offset-4 hover:text-brand hover:underline"
        >
          Submit another enquiry
        </button>
      </motion.div>
    );
  }

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: compact ? 0 : 0.35, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      data-testid="lead-form"
      className="card-soft p-5 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.45)] sm:p-7"
    >
      <div className="flex items-center gap-2">
        <span className="rounded-full bg-mint px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-brandDark">
          Free
        </span>
        <span className="text-xs font-semibold text-body">No obligation consultation</span>
      </div>
      <h3 className="mt-2.5 font-display text-lg font-bold leading-snug text-ink sm:mt-3 sm:text-2xl">
        Get a shortlist matched to your budget
      </h3>
      <p className="mt-1.5 hidden text-sm text-body sm:block">
        One advisor. One honest comparison. No spam.
      </p>

      <div className="mt-4 space-y-3 sm:mt-6 sm:space-y-4">
        <div>
          <span className={labelHideMobile}>Your name</span>
          <input
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Your name"
            data-testid="lead-name-input"
            className={fieldCls}
          />
        </div>
        <div>
          <span className={labelHideMobile}>Mobile number</span>
          <input
            required
            value={form.phone}
            onChange={set("phone")}
            placeholder="Mobile number (+91)"
            inputMode="tel"
            data-testid="lead-phone-input"
            className={fieldCls}
          />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-4">
          <div>
            <span className={labelCls}>Preferred location</span>
            <select value={form.corridor} onChange={set("corridor")} data-testid="lead-corridor-select" className={fieldCls}>
              {CORRIDORS.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
              <option value="other">Not sure yet</option>
            </select>
          </div>
          <div>
            <span className={labelCls}>Budget</span>
            <select value={form.budget} onChange={set("budget")} data-testid="lead-budget-select" className={fieldCls}>
              {BUDGETS.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <span className={labelCls}>Buying timeline</span>
          <select value={form.timeline} onChange={set("timeline")} data-testid="lead-timeline-select" className={fieldCls}>
            {TIMELINES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="lead-submit-btn"
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3.5 text-sm font-bold text-white transition-colors hover:bg-brandDark disabled:opacity-60 sm:mt-6"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {loading ? "Sending" : "Request my shortlist"}
      </button>
      <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-muted2">
        <ShieldCheck className="h-3 w-3" /> Your details stay with your advisor only
      </p>
    </motion.form>
  );
};
